import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo } from 'react'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import lineItemStyles from '../LineItem.module.scss'
import styles from './Tag.module.scss'

/**
 * The `Tag`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const Tag = forwardRef((props, forwardedRef) => {
  const {
    Tag: TagNode,
    children,
    className,
    tooltipContent,
    useTooltip,
    ...rest
  } = props
  const ref = useFallbackRef(forwardedRef)
  const classNameOutput = useMemo(
    () => classNames([className, lineItemStyles.tag, styles.tag]),
    [className]
  )

  if (useTooltip) {
    return (
      <Tooltip.Target
        ref={ref}
        Tag={TagNode}
        {...rest}
        tooltipContent={tooltipContent}
        className={classNameOutput}
      >
        {children}
      </Tooltip.Target>
    )
  }

  return (
    <TagNode ref={ref} {...rest} className={classNameOutput}>
      {children}
    </TagNode>
  )
})

// Display name
Tag.displayName = 'Tag'

/** @type {object} */
Tag.propTypes = {
  Tag: PropTypes.elementType,
  children: PropTypes.node,
  className: PropTypes.string,
  useTooltip: PropTypes.bool,
  tooltipContent: PropTypes.node
}

/** @type {object} */
Tag.defaultProps = {
  Tag: 'div',
  children: null,
  className: '',
  useTooltip: false,
  tooltipContent: null
}

// Memoize
export default memo(Tag)
